import { type ChangeEvent } from "react";
import { Controller, useController } from "react-hook-form";
import {
  assertFieldName,
  makeFieldOptional,
  makeFieldRequired,
  makeFieldTypeOptional,
  makeFieldTypeRequired,
  type PathArray,
} from "common/utils/formStructure";
import Disclaimer from "../../../../../../../Common/Disclaimer";
import { Checkbox } from "../../../../../../../Inputs";
import { Text } from "../../../../../../../Inputs/react-hook-form";
import {
  getSchemaPropertyFormPath,
  getUiSchemaPropertyFormPath,
  useBuilderFormContext,
  useBuilderWatch,
} from "../../../form/form";
import { getIsFieldRequired, getRequiredPropertyFormPath } from "../required";

type FieldSettingsConfigProps = {
  fieldPath: PathArray;
  isFormulaField?: boolean;
  isLabelRequired?: boolean;
  isModuleInput?: boolean;
  isRequiredFieldDisabled?: boolean;
  isReadOnly?: boolean;
  onRequiredChange?: (event: ChangeEvent<HTMLInputElement>) => void;
};

export function FieldSettingsConfig({
  fieldPath,
  isFormulaField,
  isLabelRequired = true,
  isModuleInput,
  isRequiredFieldDisabled,
  isReadOnly,
  onRequiredChange,
}: FieldSettingsConfigProps) {
  const { register } = useBuilderFormContext();

  const fieldLabelPropertyFormPath = getSchemaPropertyFormPath(
    fieldPath,
    "title",
  );
  const fieldLabelController = useController({
    name: fieldLabelPropertyFormPath,
  });
  const fieldLabelError = fieldLabelController.fieldState.error;

  const tooltipTextPropertyFormPath = getUiSchemaPropertyFormPath(
    fieldPath,
    "tooltipText",
  );

  return (
    <>
      {isFormulaField && (
        <Disclaimer
          message="This field is derived from a formula and cannot be modified directly. You may still customize supporting text, default values, and permissions."
          margin={false}
        />
      )}
      <Text
        label="Field label"
        required={isLabelRequired}
        error={fieldLabelError?.message}
        disabled={isReadOnly}
        {...register(fieldLabelPropertyFormPath)}
      />
      {!isFormulaField && (
        <RequiredFieldCheckbox
          fieldPath={fieldPath}
          disabled={isReadOnly || isRequiredFieldDisabled || isModuleInput}
          onRequiredChange={onRequiredChange}
        />
      )}
      <Text
        label="Tooltip text"
        disabled={isReadOnly}
        {...register(tooltipTextPropertyFormPath)}
      />
    </>
  );
}

type RequiredFieldCheckboxProps = {
  fieldPath: PathArray;
  disabled?: boolean;
  onRequiredChange?: (event: ChangeEvent<HTMLInputElement>) => void;
};

function RequiredFieldCheckbox({
  fieldPath,
  disabled,
  onRequiredChange,
}: RequiredFieldCheckboxProps) {
  const { control, setValue } = useBuilderFormContext();
  const requiredPropertyFormPath = getRequiredPropertyFormPath(fieldPath);
  const typePropertyFormPath = getSchemaPropertyFormPath(fieldPath, "type");
  const currentType = useBuilderWatch(typePropertyFormPath);
  const fieldName = assertFieldName(fieldPath);

  return (
    <Controller
      control={control}
      name={requiredPropertyFormPath}
      render={({ field }) => {
        const required = (field.value as Array<string> | undefined) ?? [];
        const isRequired = getIsFieldRequired(fieldName, required);

        return (
          <Checkbox
            label="Required field"
            value={isRequired}
            disabled={disabled}
            additionalOnChange={onRequiredChange}
            onChange={checked => {
              // When a field's required status changes, it is updated in two
              // places:
              // 1. the `required` array at the parent's field path
              // 2. the `type` property in the field's schema
              //
              // When a field is made required, its name is added to the
              // `required` array and the `type` property is updated to remove
              // "null". The inverse happens when a field is made optional.
              const newRequiredArray =
                checked ?
                  makeFieldRequired(fieldName, required)
                : makeFieldOptional(fieldName, required);

              // object-type fields (User, PropertyMarketValue, NumberWithUnits)
              // are not nullable at the field level, so keep their types as
              // just "object" when they're made optional, relying on their lack
              // of inclusion in the "required" array to produce the correct
              // behavior
              const newType =
                checked ? makeFieldTypeRequired(currentType)
                : currentType !== "object" ? makeFieldTypeOptional(currentType)
                : currentType;

              field.onChange(newRequiredArray);
              setValue(typePropertyFormPath, newType, {
                shouldValidate: true,
              });
            }}
          />
        );
      }}
    />
  );
}
